import type { Report, Transport } from './types'

/** Remove internal scratch fields that must never leave the client. */
function sanitize(report: Report): Report {
  delete report.target._unreadable_sheets
  return report
}

/** Deliver a report via the configured transport. Throws on failure (caller shows retry/download UI). */
export async function send(report: Report, transport: Transport): Promise<void> {
  sanitize(report)
  switch (transport.type) {
    case 'post': {
      const res = await fetch(transport.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(transport.token ? { 'X-QCLens-Token': transport.token } : {}),
        },
        body: JSON.stringify(report),
      })
      if (!res.ok) throw new Error(`Sink responded ${res.status}`)
      return
    }
    case 'download':
      download(report)
      return
    case 'callback':
      await transport.fn(report)
      return
    case 'postMessage':
      window.postMessage({ type: 'qc-lens:report', report }, transport.targetOrigin ?? location.origin)
      return
  }
}

/** Local download fallback — a review session must never lose work (ui-ux-plan §5.4). */
export function download(report: Report): void {
  sanitize(report)
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `qc-lens-report-${report.report_id.slice(0, 8)}.json`
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}
